import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "./api";
import TermosTexto from "./TermosTexto";
import AuthVisual from "./AuthVisual";
import LanguageSwitcher from "./LanguageSwitcher";
import "../estilo.css";

export default function Cadastro() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Dados pessoais
  const [regiao, setRegiao] = useState("BR");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [passwd, setPasswd] = useState("");
  const [passwdConfirm, setPasswdConfirm] = useState("");
  const [codigoPromocional, setCodigoPromocional] = useState("");

  // Robôs e tier
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [selectedRobots, setSelectedRobots] = useState(new Set());
  const [selectedCombo, setSelectedCombo] = useState(false);
  const [selectedTier, setSelectedTier] = useState("starter");

  // Termos e estado
  const [termosAceitos, setTermosAceitos] = useState(false);
  const [showTermos, setShowTermos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDados, setLoadingDados] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "pt-BR";
  const formatCurrency = (value, currency = "BRL") =>
    new Intl.NumberFormat(locale, { style: "currency", currency }).format(Number(value || 0));
  const getProductDescription = (product) =>
    i18n.resolvedLanguage === "en"
      ? t(`plans.productDescriptions.${String(product.codigo || "").toLowerCase()}`, {
          defaultValue: product.descricao,
        })
      : product.descricao;

  useEffect(() => {
    carregarDados();
  }, []);

  // Receber seleção da página de planos, inclusive após navegação completa.
  useEffect(() => {
    let selection = location.state;
    if (!selection) {
      try {
        selection = JSON.parse(sessionStorage.getItem("quantumPlanSelection") || "null");
      } catch {
        selection = null;
      }
    }
    sessionStorage.removeItem("quantumPlanSelection");
    if (selection) {
      const { selectedRobots: robots, selectedCombo: combo, selectedTier: tier } = selection;
      if (robots && robots.length > 0) setSelectedRobots(new Set(robots));
      if (combo) setSelectedCombo(true);
      if (tier) setSelectedTier(tier);
    }
  }, [location.state]);

  const carregarDados = async () => {
    try {
      const [resProd, resPac] = await Promise.all([
        api.get("/api/produtos-ativos"),
        api.get("/api/pacotes-ativos"),
      ]);
      setProdutos(resProd.data || []);
      setPacotes(resPac.data || []);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    } finally {
      setLoadingDados(false);
    }
  };

  const isBrasil = regiao === "BR";

  // Formatadores
  const formatPhone = (num) => {
    if (!num) return "";
    if (num.length <= 2) return `(${num}`;
    if (num.length <= 6) return `(${num.slice(0, 2)}) ${num.slice(2)}`;
    if (num.length <= 10) return `(${num.slice(0, 2)}) ${num.slice(2, 6)}-${num.slice(6)}`;
    return `(${num.slice(0, 2)}) ${num.slice(2, 7)}-${num.slice(7)}`;
  };

  const formatCPF = (n) => {
    if (!n) return "";
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
  };

  const handlePhone = (value) => {
    if (isBrasil) {
      const clean = value.replace(/\D/g, "").slice(0, 11);
      setPhone(formatPhone(clean));
    } else {
      setPhone(value);
    }
  };

  const handleCpf = (value) => {
    if (isBrasil) {
      const clean = value.replace(/\D/g, "").slice(0, 11);
      setCpf(formatCPF(clean));
    } else {
      setCpf(value);
    }
  };

  const validarEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);

  const validarCPF = (cpfRaw) => {
    const c = cpfRaw.replace(/[^\d]+/g, "");
    if (c.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(c)) return false;

    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(c.charAt(i), 10) * (10 - i);
    let resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(c.charAt(9), 10)) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(c.charAt(i), 10) * (11 - i);
    resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(c.charAt(10), 10)) return false;

    return true;
  };

  // Seleção de robôs
  const toggleRobot = (id) => {
    setSelectedCombo(false);
    setSelectedRobots((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCombo = () => {
    setSelectedCombo(!selectedCombo);
    if (!selectedCombo) setSelectedRobots(new Set());
  };

  // Cálculo do resumo
  const calcResumo = () => {
    const pacote = pacotes[0];
    if (selectedCombo && pacote && pacote.precos[selectedTier]) {
      const p = pacote.precos[selectedTier];
      return { brl: Number(p.valor_brl), usd: Number(p.valor_usd), slots: p.slots_inclusos };
    }
    if (selectedRobots.size > 0) {
      let brl = 0, usd = 0, slots = 0;
      selectedRobots.forEach((id) => {
        const prod = produtos.find((p) => p.id === id);
        if (prod && prod.precos[selectedTier]) {
          brl += Number(prod.precos[selectedTier].valor_brl);
          usd += Number(prod.precos[selectedTier].valor_usd);
          slots = prod.precos[selectedTier].slots_inclusos;
        }
      });
      return { brl, usd, slots };
    }
    return null;
  };

  const resumo = calcResumo();

  // Submit
  const handleCadastro = async () => {
    setMessage("");
    setError(false);

    if (!termosAceitos) {
      setError(true);
      setMessage(t('register.validations.terms'));
      return;
    }

    if (!nome || !passwd || !email || !phone || !cpf) {
      setError(true);
      setMessage(t('register.validations.required'));
      return;
    }

    if (passwd.length < 6) {
      setError(true);
      setMessage(t('register.validations.passwordLength'));
      return;
    }

    if (passwd !== passwdConfirm) {
      setError(true);
      setMessage(t('register.validations.passwordMatch'));
      return;
    }

    if (!validarEmail(email)) {
      setError(true);
      setMessage(t('register.validations.email'));
      return;
    }

    if (isBrasil) {
      const cleanCPF = cpf.replace(/\D/g, "");
      if (!validarCPF(cleanCPF)) {
        setError(true);
        setMessage(t('register.validations.cpf'));
        return;
      }
    }

    const hasRobots = selectedCombo || selectedRobots.size > 0;
    if (!hasRobots) {
      setError(true);
      setMessage(t('register.validations.robots'));
      return;
    }

    setLoading(true);

    const cleanPhone = isBrasil ? phone.replace(/\D/g, "") : phone.trim();
    const cpfParaEnvio = isBrasil ? cpf.replace(/\D/g, "") : cpf.trim();

    const pacote = pacotes[0];
    const body = {
      nome,
      passwd,
      email,
      phone: cleanPhone,
      cpf: cpfParaEnvio,
      tier: selectedTier,
    };

    const codigoPromo = codigoPromocional.trim();
    if (codigoPromo) {
      body.codigo_promocional = codigoPromo;
    }

    if (selectedCombo && pacote) {
      body.pacote_id = pacote.id;
    } else {
      body.produtos = Array.from(selectedRobots);
    }

    try {
      const response = await api.post("/cadastro", body);
      const ativado = response.data?.ativado === true;
      const trialAte = response.data?.trial_ate || null;

      setError(false);
      // Conta ativada por cupom nao deve ser mandada a "entrar em contato".
      setMessage(
        ativado
          ? t('register.successActivated', {
              trial: trialAte ? t('register.trialUntil', { date: trialAte }) : '',
            }).trim()
          : t('register.success')
      );

      setTimeout(() => {
        window.location.replace("/login");
      }, 2500);
    } catch (err) {
      setError(true);
      // O servidor devolve um `code` estavel; a traducao vive no locale, para
      // que o usuario em ingles nao receba a mensagem crua em portugues.
      const code = err.response?.data?.code;
      setMessage(
        t(`register.errors.${code || 'ERRO_INTERNO'}`, {
          defaultValue:
            i18n.resolvedLanguage === 'en'
              ? t('register.serverError')
              : err.response?.data?.message || t('register.serverError'),
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-page">
      <LanguageSwitcher className="auth-language-switcher" />
      <AuthVisual className="cadastro-visual" />

      <div className="cadastro-container">
        <h1 className="cadastro-titulo">{t('register.title')}</h1>
        <p className="cadastro-subtitulo">{t('register.subtitle')}</p>

        {/* Aviso */}
        <div className="cadastro-info-box">
          <p>
            <strong>{t('register.important')}</strong> {t('register.notice')}
          </p>
        </div>

        {/* ========== DADOS PESSOAIS ========== */}
        <div className="cadastro-section-label">{t('register.yourData')}</div>

        <div className="input-container">
          <select
            className="cadastro-input"
            value={regiao}
            onChange={(e) => setRegiao(e.target.value)}
            aria-label={t('register.region')}
          >
            <option value="BR">{t('register.regions.br')}</option>
            <option value="AM_SUL">{t('register.regions.southAmerica')}</option>
            <option value="AM_NORTE">{t('register.regions.northAmerica')}</option>
            <option value="EUROPA">{t('register.regions.europe')}</option>
            <option value="ASIA">{t('register.regions.asia')}</option>
            <option value="OUTROS">{t('register.regions.other')}</option>
          </select>

          <input
            className="cadastro-input"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={t('register.fullName')}
            aria-label={t('register.fullName')}
          />

          <input
            className="cadastro-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            aria-label="Email"
          />

          <input
            className="cadastro-input"
            type="text"
            value={phone}
            onChange={(e) => handlePhone(e.target.value)}
            aria-label={t('register.phone')}
            placeholder={
              isBrasil ? t('register.phoneBrazil') : t('register.phoneInternational')
            }
          />

          <input
            className="cadastro-input"
            type="text"
            value={cpf}
            onChange={(e) => handleCpf(e.target.value)}
            aria-label={isBrasil ? "CPF" : t('register.idDocument')}
            placeholder={
              isBrasil ? "CPF" : t('register.idPlaceholder')
            }
          />

          <input
            className="cadastro-input"
            type="password"
            value={passwd}
            onChange={(e) => setPasswd(e.target.value)}
            placeholder={t('register.password')}
            aria-label={t('register.passwordLabel')}
          />

          <input
            className="cadastro-input"
            type="password"
            value={passwdConfirm}
            onChange={(e) => setPasswdConfirm(e.target.value)}
            placeholder={t('register.confirmPassword')}
            aria-label={t('register.confirmPassword')}
          />

          <input
            className="cadastro-input"
            type="text"
            value={codigoPromocional}
            onChange={(e) => setCodigoPromocional(e.target.value.toUpperCase())}
            placeholder={t('register.promoCode')}
            aria-label={t('register.promoCode')}
          />
        </div>

        {/* ========== ROBÔS ========== */}
        <div className="cadastro-section-label">{t('register.chooseRobots')}</div>

        {loadingDados ? (
          <p style={{ textAlign: "center", color: "#777" }}>{t('common.loading')}</p>
        ) : (
          <>
            <div className="cadastro-robots-grid">
              {produtos.map((prod) => (
                <div
                  key={prod.id}
                  className={`cadastro-robot-chip ${
                    selectedRobots.has(prod.id) ? "selected" : ""
                  }`}
                  onClick={() => toggleRobot(prod.id)}
                >
                  <span className="cadastro-robot-check">
                    {selectedRobots.has(prod.id) ? "✓" : ""}
                  </span>
                  <strong>{prod.nome}</strong>
                  <span className="cadastro-robot-desc">{getProductDescription(prod)}</span>
                </div>
              ))}
            </div>

            {pacotes[0] && (
              <div
                className={`cadastro-combo-bar ${selectedCombo ? "selected" : ""}`}
                onClick={toggleCombo}
              >
                <strong>{pacotes[0].nome}</strong>
                <span> — {i18n.resolvedLanguage === 'en' ? t('plans.packageDescription') : pacotes[0].descricao}</span>
              </div>
            )}
          </>
        )}

        {/* ========== TIER ========== */}
        <div className="cadastro-section-label">{t('register.operationSize')}</div>

        <div className="cadastro-tier-grid">
          {[
            { key: "starter", nome: t('plans.tiers.starter.name'), slots: 2 },
            { key: "trader", nome: t('plans.tiers.trader.name'), slots: 5 },
            { key: "pro", nome: t('plans.tiers.pro.name'), slots: 10 },
          ].map((tier) => (
            <div
              key={tier.key}
              className={`cadastro-tier-chip ${
                selectedTier === tier.key ? "selected" : ""
              }`}
              onClick={() => setSelectedTier(tier.key)}
            >
              <strong>{tier.nome}</strong>
              <span>{t('register.accounts', { count: tier.slots })}</span>
            </div>
          ))}
        </div>

        {/* ========== RESUMO ========== */}
        {resumo && (
          <div className="cadastro-resumo">
            <span>
              {t('register.summary', {
                selection: selectedCombo
                  ? t('register.fullPackage')
                  : t('register.robotCount', { count: selectedRobots.size }),
                tier: t(`plans.tiers.${selectedTier}.name`),
              })}
            </span>
            <strong>
              {formatCurrency(resumo.brl)}{t('plans.perMonth')}
              <span className="cadastro-resumo-usd">
                {" "}({formatCurrency(resumo.usd, 'USD')})
              </span>
            </strong>
          </div>
        )}

        {/* ========== TERMOS ========== */}
        <div className="cadastro-termos-centralizado">
          <input
            type="checkbox"
            checked={termosAceitos}
            onChange={(e) => setTermosAceitos(e.target.checked)}
          />
          <label>
            {t('register.acceptPrefix')}{" "}
            <button
              type="button"
              className="termos-link-btn"
              onClick={() => setShowTermos(true)}
            >
              {t('register.terms')}
            </button>
          </label>
        </div>

        <button
          className="cadastro-button"
          onClick={handleCadastro}
          disabled={loading}
        >
          {loading ? t('register.submitting') : t('register.submit')}
        </button>

        {message && (
          <p className={`cadastro-message ${error ? "error" : "success"}`}>
            {message}
          </p>
        )}

        <Link reloadDocument to="/" className="cadastro-link">
          {t('common.back')}
        </Link>

        {/* MODAL DOS TERMOS */}
        {showTermos && (
          <div
            className="termos-modal-overlay"
            onClick={() => setShowTermos(false)}
          >
            <div className="termos-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('register.terms')}</h3>
              <div className="termos-text"><TermosTexto /></div>
              <button
                className="footer-terms-btn"
                onClick={() => setShowTermos(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

